import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Modal,
  Button,
  IconButton,
  Paper,
  LinearProgress
} from '@material-ui/core';
import {
  Close as CloseIcon,
} from '@material-ui/icons';

import { useUploadStyles } from '../styles';

const UploadModal = ({ open, handleClose, addAttachments }) => {
  const classes = useUploadStyles();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [activeDragOver, setActiveDragOver] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDragOver = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    setActiveDragOver(true);
  };

  const handleDragLeave = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    setActiveDragOver(false);
  };

  const handleDrop = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();

    const dt = ev.dataTransfer;
    const files = dt.files;

    setSelectedFiles([
      ...selectedFiles,
      ...Object.keys(files).map((key) => files[key]),
    ]);
    setActiveDragOver(false);
  };

  const handleChange = (ev) => {
    const files = ev.target.files;

    setSelectedFiles([
      ...selectedFiles,
      ...Object.keys(files).map((key) => files[key]),
    ]);
  };

  const processImages = useCallback(() => {
    return Promise.all(
      selectedFiles.map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = (ev) => {
            resolve(ev.target.result);
          };
          reader.readAsDataURL(file);
        });
      })
    );
  }, [selectedFiles]);

  const getProccessedImages = useCallback(async () => {
    const images = await processImages();
    setPreviews(images);
  }, [processImages]);

  const uploadFiles = () => {
    setLoading(true);

    setTimeout(() => {
      addAttachments(previews);
      setPreviews([]);
      setSelectedFiles([]);
      handleClose();
      setLoading(false);
    }, 3000);
  };

  const removeFile = (index) => {
    const files = [...selectedFiles];
    files.splice(index, 1);

    setSelectedFiles(files);
  };

  const renderPreview = (image, index) => {
    return (
      <Paper elevation={2} className={classes.previewImg} key={index}>
        <img src={image} alt={`Brand upload ${index + 1}`} />
        <IconButton
          size="small"
          className={classes.previewRemove}
          onClick={() => removeFile(index)}
        >
          <CloseIcon />
        </IconButton>
      </Paper>
    );
  };

  useEffect(() => {
    if (selectedFiles.length > 0) {
      getProccessedImages();
    } else {
      setPreviews([]);
    }
  }, [selectedFiles, getProccessedImages]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="Attachments Modal"
      aria-describedby="Brand images attachments modal"
      className={classes.modalWrapper}
    >
      <div className={classes.modal}>
        {loading ? (
          <center>
            <h4 style={{ margin: '0 0 15px' }}>Uploading files. Please wait...</h4>
            <LinearProgress />
          </center>
        ) : (
            <>
              <h3 className={classes.modalTitle}>Upload Image(s):</h3>
              <div>
                <label
                  className={clsx({
                    [classes.uploadArea]: true,
                    [classes.uploadAreaActive]: activeDragOver,
                  })}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input type="file" multiple onChange={handleChange} />
                  Drag'n'drop images, or click to select files
                </label>
              </div>
              {previews.length > 0 && (
                <div className={classes.previewsWrapper}>
                  {previews.map(renderPreview)}
                </div>
              )}
              <div className={classes.modalActions}>
                <Button size="small" color="primary" variant="contained" onClick={uploadFiles}>
                  Upload
                </Button>
                <Button size="small" color="primary" onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </>
          )
        }
      </div>
    </Modal >
  );
};

UploadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  addAttachments: PropTypes.func.isRequired,
};

export default UploadModal;
